<template>
  <div>
    <div class="flex flex-col gap-3 mb-4 sm:mb-6 sm:flex-row sm:justify-between sm:items-center">
      <h2 class="text-xl sm:text-2xl font-bold text-gray-900">Regeln verwalten</h2>
      <button
        @click="openCreateModal"
        class="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
      >
        Neue Regel
      </button>
    </div>

    <div v-if="loading" class="text-center py-8">Laden...</div>
    <div v-else-if="error" class="rounded-md bg-red-50 p-4 mb-4">
      <p class="text-sm text-red-800">{{ error }}</p>
    </div>
    <div v-else>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reihenfolge</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
              <th class="sticky right-0 bg-gray-50 px-3 py-3 sm:px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="rule in rules" :key="rule.id">
              <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ rule.id }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ rule.type }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ rule.order }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 text-sm text-gray-900">{{ rule.ruleDescription }}</td>
              <td class="sticky right-0 bg-white px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  @click="openEditModal(rule)"
                  class="text-indigo-600 hover:text-indigo-900 mr-3 sm:mr-4"
                >
                  Bearbeiten
                </button>
                <button
                  @click="confirmDelete(rule)"
                  class="text-red-600 hover:text-red-900"
                >
                  Löschen
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed z-10 inset-0 overflow-y-auto" @click.self="closeModal">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form @submit.prevent="saveRule">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                {{ editingRule ? 'Regel bearbeiten' : 'Neue Regel' }}
              </h3>
              <div v-if="formError" class="rounded-md bg-red-50 p-3 mb-4">
                <p class="text-sm text-red-800">{{ formError }}</p>
              </div>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Typ</label>
                  <select
                    v-model="formData.type"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="Public">Öffentlich</option>
                    <option value="Game">Spiel</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Reihenfolge</label>
                  <input
                    v-model.number="formData.order"
                    type="number"
                    required
                    min="0"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Beschreibung</label>
                  <textarea
                    v-model="formData.ruleDescription"
                    required
                    rows="4"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  ></textarea>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Speichern
              </button>
              <button
                type="button"
                @click="closeModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import adminService, { type AdminRule } from '@/services/AdminService'

const rules = ref<AdminRule[]>([])
const loading = ref(false)
const error = ref('')
const formError = ref('')
const showModal = ref(false)
const editingRule = ref<AdminRule | null>(null)
const formData = ref({
  type: '',
  ruleDescription: '',
  order: 0
})

const loadRules = async () => {
  loading.value = true
  error.value = ''
  try {
    rules.value = await adminService.getRules()
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Fehler beim Laden der Regeln'
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingRule.value = null
  formData.value = {
    type: '',
    ruleDescription: '',
    order: 0
  }
  showModal.value = true
}

const openEditModal = (rule: AdminRule) => {
  editingRule.value = rule
  formData.value = {
    type: rule.type,
    ruleDescription: rule.ruleDescription,
    order: rule.order
  }
  showModal.value = true
}

const closeModal = () => {
  formError.value = ''
  showModal.value = false
  editingRule.value = null
}

const saveRule = async () => {
  formError.value = ''
  try {
    if (editingRule.value) {
      await adminService.updateRule(editingRule.value.id, formData.value)
    } else {
      await adminService.createRule(formData.value)
    }
    closeModal()
    await loadRules()
  } catch (err: any) {
    formError.value = err.response?.data?.error || 'Fehler beim Speichern der Regel'
  }
}

const confirmDelete = async (rule: AdminRule) => {
  if (confirm('Möchten Sie diese Regel wirklich löschen?')) {
    try {
      await adminService.deleteRule(rule.id)
      await loadRules()
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Fehler beim Löschen der Regel'
    }
  }
}

onMounted(() => {
  loadRules()
})
</script>

